import React, { forwardRef, useMemo } from 'react';

import { SerializedStyles } from '@emotion/react';
import { Box, Typography, Container } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';

import { HEADER_IMG_SRC_SET } from '../../const';
import styled from '../../styled';
import { colorsPalette, LARGE_MARGIN_PX, MIDDLE_MARGIN_PX } from '../../theme';
import { generateRandomImageUrl } from '../../utils/helpers';
import IKImage from '../IKImage/IKImage';

interface HeaderProps {
  title?: string;
  children?: React.ReactNode;
  imageUrl?: string;
  imageSrcset?: any[];
  image?: any;
  tenantName?: string | null;
  wrapperCss?: SerializedStyles | (({ theme }: { theme: Theme }) => SerializedStyles);
  imageWrapperCss?: SerializedStyles | (({ theme }: { theme: Theme }) => SerializedStyles);
  contentWrapperCss?: SerializedStyles | (({ theme }: { theme: Theme }) => SerializedStyles);
  contentWrapperClass?: string;
  titleCss?: SerializedStyles | (({ theme }: { theme: Theme }) => SerializedStyles);
}

const Header = forwardRef<HTMLDivElement, HeaderProps>(
  (
    {
      title,
      children,
      imageUrl,
      imageSrcset = HEADER_IMG_SRC_SET,
      image,
      tenantName,
      wrapperCss,
      imageWrapperCss,
      contentWrapperCss,
      contentWrapperClass = '',
      titleCss,
    },
    ref,
  ) => {
    const currentImage = useMemo(() => generateRandomImageUrl(tenantName), [tenantName]);

    return (
      <Wrapper ref={ref} withImage={true} optionalStyles={wrapperCss}>
        <ImageWrapper optionalStyles={imageWrapperCss}>
          {image ? (
            <Img src={image} alt={title} />
          ) : (
            <StyledIKImage
              path={imageUrl || currentImage}
              alt={title}
              srcset={imageSrcset}
              sizes="100vw"
              draggable={false}
            />
          )}
        </ImageWrapper>
        <DefaultBackground />
        <ContentWrapper className={contentWrapperClass} optionalStyles={contentWrapperCss}>
          {title && (
            <Container>
              <Title
                data-testid="pageTitle"
                className="headerTitle"
                variant="h1"
                optionalStyles={titleCss}
              >
                {title}
              </Title>
            </Container>
          )}
          {children}
        </ContentWrapper>
      </Wrapper>
    );
  },
);

export default Header;

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles' && prop !== 'withImage',
})`
  position: relative;
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    background: ${({ theme }) => theme.palette.secondary.dark};
    opacity: 0.7;
    z-index: 2;
    ${({ theme }) => theme.breakpoints.up('md')} {
      width: 70%;
      background: linear-gradient(
        90deg,
        ${colorsPalette.blueDark} 0%,
        ${alpha(colorsPalette.blueDark, 0.0001)} 100%
      );
      opacity: 1;
    }
  }
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: ${({ withImage }) => (withImage ? '100%' : '370px')};
    left: 0;
    bottom: -1px;
    background: linear-gradient(
      0deg,
      ${colorsPalette.blueDark} 0%,
      ${alpha(colorsPalette.blueDark, 0.0001)} 100%
    );
  }
  ${({ optionalStyles }) => optionalStyles}
`;
const ContentWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  padding-top: 100px;
  padding-bottom: ${LARGE_MARGIN_PX};
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;
  position: relative;
  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-top: 200px;
    padding-bottom: ${LARGE_MARGIN_PX};
  }
  // TODO: remove '&&' after full migration Header component to emotion
  && {
    ${({ optionalStyles }) => optionalStyles}
  }
`;
const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  && {
    line-height: 1.2;
    font-size: 32px;
    font-weight: bold;
    color: ${colorsPalette.white};
    text-align: center;

    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: 40px;
    }

    margin-bottom: ${MIDDLE_MARGIN_PX};
    ${({ optionalStyles }) => optionalStyles}
  }
`;

const ImageWrapper = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  && {
    ${({ optionalStyles }) => optionalStyles}
  }
`;
const Img = styled('img')`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const StyledIKImage = styled(IKImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const DefaultBackground = styled(Box)`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: radial-gradient(
    82% 64.09% at 50% 50%,
    ${alpha(colorsPalette.violet, 0.396078)} 0%,
    ${alpha(colorsPalette.turquoiseLight, 0.4)} 100%
  );
`;
